// Persisted UI preferences.
//
// These are conveniences, never data we can't rebuild: reading localStorage
// throws outright in browsers set to block site data, so every access here
// falls back to a default rather than propagating the failure.

export type PeerGroupSortField = 'name' | 'createdAt';

export interface PeerGroupSort {
  field: PeerGroupSortField;
  descending: boolean;
}

const PEER_GROUP_SORT_KEY = 'unrulr_peer_group_sort';

const SORT_FIELDS: PeerGroupSortField[] = ['name', 'createdAt'];

export const DEFAULT_PEER_GROUP_SORT: PeerGroupSort = {
  field: 'name',
  descending: false
};

/**
 * Returns the stored sort a raw localStorage value represents, or the default
 * when it is absent, malformed, or names a field we no longer sort by.
 */
export const parsePeerGroupSort = (raw: string | null): PeerGroupSort => {
  if (!raw) {
    return DEFAULT_PEER_GROUP_SORT;
  }

  try {
    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object') {
      return DEFAULT_PEER_GROUP_SORT;
    }

    if (!SORT_FIELDS.includes(parsed.field)) {
      return DEFAULT_PEER_GROUP_SORT;
    }

    return { field: parsed.field, descending: !!parsed.descending };
  } catch (error) {
    return DEFAULT_PEER_GROUP_SORT;
  }
};

export const loadPeerGroupSort = (): PeerGroupSort => {
  try {
    return parsePeerGroupSort(localStorage.getItem(PEER_GROUP_SORT_KEY));
  } catch (error) {
    return DEFAULT_PEER_GROUP_SORT;
  }
};

export const savePeerGroupSort = (sort: PeerGroupSort): void => {
  try {
    localStorage.setItem(PEER_GROUP_SORT_KEY, JSON.stringify(sort));
  } catch (error) {
    // A preference we can't store is not worth failing a render over.
  }
};
